/**
 * Spinner Component
 * Loading indicator with size and color variants
 */

import type { SpinnerProps } from '../types/components';
import { cn } from '../utils/cn';

const sizeStyles: Record<string, { container: string; spinner: string }> = {
  xs: { container: 'h-3 w-3', spinner: 'border-[2px]' },
  sm: { container: 'h-4 w-4', spinner: 'border-[2px]' },
  md: { container: 'h-6 w-6', spinner: 'border-[2px]' },
  lg: { container: 'h-8 w-8', spinner: 'border-[3px]' },
  xl: { container: 'h-12 w-12', spinner: 'border-[3px]' },
};

export function Spinner({
  size = 'md',
  color = 'currentColor',
  className,
  label,
}: SpinnerProps) {
  const styles = sizeStyles[size];

  return (
    <div
      className={cn('inline-flex flex-col items-center gap-2', className)}
      role="status"
      aria-label={label || 'Loading'}
    >
      <div
        className={cn(
          'animate-spin rounded-full',
          'border-transparent border-t-current border-r-current',
          styles.container,
          styles.spinner
        )}
        style={{ color }}
      >
        <span className="sr-only">{label || 'Loading...'}</span>
      </div>
      {label && (
        <span className="text-sm text-gray-500 dark:text-slate-400">{label}</span>
      )}
    </div>
  );
}

/**
 * Full Page Loading Spinner
 */
export function PageSpinner({ label = 'Loading...' }: { label?: string }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50 dark:bg-slate-950/80">
      <Spinner size="xl" label={label} />
    </div>
  );
}

/**
 * Inline Loading Spinner with Text
 */
export function LoadingText({
  text = 'Loading...',
  size = 'sm',
}: {
  text?: string;
  size?: 'xs' | 'sm' | 'md';
}) {
  return (
    <div className="inline-flex items-center gap-2 text-gray-500 dark:text-slate-400">
      <Spinner size={size} />
      <span className="text-sm">{text}</span>
    </div>
  );
}

export default Spinner;
